/** Radius of a cardinal tip (N, E, S, W) as a fraction of `size / 2`. */
const CARDINAL_TIP_RATIO = 1.0
/** Radius of a diagonal tip as a fraction of `size / 2`. */
const DIAGONAL_TIP_RATIO = 0.74
/** Radius of a valley between two tips as a fraction of `size / 2`. */
const VALLEY_RATIO = 0.41

/**
 * Radius of the centre circle as a fraction of `size / 2`.
 *
 * The ratios here are traced from Abbott Lyon's birthstone PNGs: tip 25.75 px, diagonal 19,
 * valley 10.5, circle 8, on a 57 px canvas.
 */
export const BIRTHSTONE_STAR_CIRCLE_RATIO = 0.31

export type GetBirthstoneStarPointsOptions = {
  /** Centre x. */
  cx: number
  /** Centre y. */
  cy: number
  /** Star width (tip to tip). */
  size: number
}

/**
 * The 16 vertices of the birthstone star (an 8-point star), clockwise from north at 22.5° steps.
 * Even indices are tips (cardinal on multiples of 4, diagonal otherwise); odd indices are valleys.
 * One builder so the DOM SVG and the canvas preview draw the same shape.
 *
 * @param options - Options object.
 * @param options.cx - Centre x.
 * @param options.cy - Centre y.
 * @param options.size - Star width (tip to tip).
 * @returns The vertices as `[x, y]` pairs.
 */
export function getBirthstoneStarPoints({ cx, cy, size }: GetBirthstoneStarPointsOptions): Array<[number, number]> {
  const halfSize = size / 2

  return Array.from({ length: 16 }, (_, index) => {
    const ratio = index % 2 === 1 ? VALLEY_RATIO : index % 4 === 0 ? CARDINAL_TIP_RATIO : DIAGONAL_TIP_RATIO
    const angle = (index * Math.PI) / 8
    return [cx + Math.sin(angle) * halfSize * ratio, cy - Math.cos(angle) * halfSize * ratio] as [number, number]
  })
}
